import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Heart, Trash2, Wine, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Favorites() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await axios.get(`${API}/favorites`, { headers: getAuthHeaders() });
        setFavorites(response.data);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        toast.error('Failed to load favorites');
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [getAuthHeaders]);

  const removeFavorite = async (cocktailId) => {
    try {
      await axios.delete(`${API}/favorites/${cocktailId}`, { headers: getAuthHeaders() });
      setFavorites(prev => prev.filter(f => f.cocktail_id !== cocktailId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="favorites-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-3 flex items-center gap-3">
          <Heart className="w-10 h-10 text-[#EF4444]" />
          My Favorites
        </h1>
        <p className="text-[#A3A3A3] text-lg">
          Your saved cocktail collection
        </p>
      </div>

      {/* Favorites Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-[320px] shimmer rounded-2xl" />
          ))}
        </div>
      ) : favorites.length === 0 ? (
        <div className="empty-state glass rounded-2xl py-16">
          <Heart className="w-16 h-16 text-[#525252] mb-6" />
          <h3 className="text-xl font-bold text-[#F5F5F5] mb-2">No favorites yet</h3>
          <p className="text-[#A3A3A3] mb-6 max-w-md">
            Start exploring cocktails and save your favorites for quick access
          </p>
          <Button
            onClick={() => navigate('/cocktails')}
            className="bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full px-8 py-6 gold-glow btn-press"
          >
            Browse Cocktails
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {favorites.map((fav, index) => (
            <div
              key={fav.id}
              className="cocktail-card animate-fadeIn"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="relative overflow-hidden">
                <img
                  src={fav.cocktail?.image_url}
                  alt={fav.cocktail?.name}
                  className="cocktail-card-image"
                />
                <button
                  onClick={() => removeFavorite(fav.cocktail_id)}
                  className="absolute top-3 right-3 w-10 h-10 rounded-full bg-[#EF4444] text-white flex items-center justify-center hover:bg-[#DC2626] transition-colors"
                  data-testid={`remove-favorite-btn-${fav.cocktail_id}`}
                >
                  <Trash2 className="w-5 h-5" />
                </button>
                <div className="absolute bottom-3 left-3">
                  <span className="category-badge">{fav.cocktail?.category}</span>
                </div>
              </div>
              
              <div 
                className="p-5 cursor-pointer"
                onClick={() => navigate(`/cocktails/${fav.cocktail_id}`)}
              >
                <h3 className="text-lg font-bold text-[#F5F5F5] mb-2">
                  {fav.cocktail?.name}
                </h3>
                <div className="flex flex-wrap gap-1 mb-4">
                  {fav.cocktail?.ingredients.slice(0, 3).map((ing, i) => (
                    <span key={i} className="text-xs text-[#A3A3A3]">
                      {ing}{i < Math.min(2, fav.cocktail.ingredients.length - 1) ? ',' : ''}
                    </span>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-full btn-press"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/cocktails/${fav.cocktail_id}`);
                  }}
                >
                  View Recipe
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      {favorites.length > 0 && (
        <div className="mt-12 glass rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#A3A3A3] text-sm">Total Favorites</p>
              <p className="text-3xl font-bold text-[#D4AF37]">{favorites.length}</p>
            </div>
            <Wine className="w-10 h-10 text-[#D4AF37]/30" />
          </div>
        </div>
      )}
    </div>
  );
}
